export const FLAVOR_PREFIX_TO_VALUE = [
  { prefix: "blueberry", value: "blueberry", emoji: "🫐" },
  { prefix: "green apple", value: "green-apple", emoji: "🍏" },
  { prefix: "tangerine", value: "tangerine", emoji: "🍊" },
  { prefix: "raspberry lemonade", value: "raspberry-lemonade", emoji: "🍇🍋" },
];

const MEDICATION_FORM_PREFERENCE_CODE = "insomnia-medication-form-preference";
const GUMMY_FLAVOR_PREFERENCE_CODE = "insomnia-medication-gummy-flavor-preference";

// Handles both the flat shape (visit.consultIntakes[].intakeJSON) and the
// nested shape used in localStorage, which wraps answers under a
// `{consultationType}-{version}` key: { "inf-2024090901": { 0: {...}, 1: {...} } }.
function findMedicationFormPreferenceQuestion(intakeJSON) {
  if (!intakeJSON || typeof intakeJSON !== "object") return null;
  for (const value of Object.values(intakeJSON)) {
    if (value?.question?.code === MEDICATION_FORM_PREFERENCE_CODE) {
      return value;
    }
    if (value && typeof value === "object") {
      for (const inner of Object.values(value)) {
        if (inner?.question?.code === MEDICATION_FORM_PREFERENCE_CODE) {
          return inner;
        }
      }
    }
  }
  return null;
}

/**
 * Returns the patient's medication-form preference from an intake answers object.
 * @param {Object} intakeJSON - Either consultAnswers (from localStorage) or a consult_intake's intakeJSON.
 * @returns {"tablets"|"gummy"|"no-preference"|null}
 */
export function getMedicationFormPreference(intakeJSON) {
  const question = findMedicationFormPreferenceQuestion(intakeJSON);
  if (!question) return null;
  const selectedIndex = Number(question.selectedIndex);
  if (selectedIndex === 0) return "tablets";
  if (selectedIndex === 1) return "gummy";
  if (selectedIndex === 2) return "no-preference";
  return null;
}

/**
 * Returns the flavor value (e.g. "blueberry") the patient selected on the intake's
 * gummy child question, or null if they didn't choose gummy or didn't pick a flavor.
 */
export function getGummyFlavorFromIntake(intakeJSON) {
  const question = findMedicationFormPreferenceQuestion(intakeJSON);
  if (!question) return null;
  const selectedIndex = Number(question.selectedIndex);
  if (selectedIndex !== 1) return null;
  const childAnswers = question[selectedIndex]?.childAnswers;
  const flavorAnswer = childAnswers?.[GUMMY_FLAVOR_PREFERENCE_CODE];
  if (!flavorAnswer?.selectedValue) return null;
  const selectedFlavorLower = flavorAnswer.selectedValue.toLowerCase();
  for (const { prefix, value } of FLAVOR_PREFIX_TO_VALUE) {
    if (selectedFlavorLower.startsWith(prefix)) return value;
  }
  return null;
}

/**
 * Visit-level helper used by the EMR: prefers the order_item subcategory (what was
 * actually ordered), falling back to the intake answer for older visits.
 * @returns {string|null} flavor value (e.g. "green-apple") or null
 */
export function getFlavorPreferenceFromVisit(visit) {
  const subcategory = visit?.orderItems?.[0]?.subcategory;
  if (subcategory) return subcategory;
  if (!visit?.consultIntakes?.length) return null;
  for (const intake of visit.consultIntakes) {
    const flavor = getGummyFlavorFromIntake(intake?.intakeJSON);
    if (flavor) return flavor;
  }
  return null;
}
